/**
 * React Hook Form + Zod Integration Hook
 * Provides common setup for form validation with RHF and Zod
 */

import { useForm, type UseFormProps, type FieldValues, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { useMemo } from 'react';

type FormSchema<TValues extends FieldValues> = z.ZodType<TValues, FieldValues>;

export interface UseRHFWithZodOptions<TValues extends FieldValues> extends Omit<UseFormProps<TValues>, 'resolver' | 'defaultValues'> {
  schema: FormSchema<TValues>;
  defaultValues?: UseFormProps<TValues>['defaultValues'];
}

/**
 * Custom hook that combines react-hook-form with Zod validation
 *
 * @example
 * const { register, handleSubmit, formState: { errors } } = useRHFWithZod({
 *   schema: myZodSchema,
 *   defaultValues: { name: '', email: '' }
 * });
 */
export function useRHFWithZod<TValues extends FieldValues>({ schema, defaultValues, ...restOptions }: UseRHFWithZodOptions<TValues>) {
  // const resolver = zodResolver<FieldValues, unknown, TValues>(schema) as Resolver<TValues>;
  const resolver = useMemo(() => zodResolver<FieldValues, unknown, TValues>(schema) as Resolver<TValues>, [schema]);

  return useForm<TValues>({
    resolver,
    defaultValues,
    mode: 'onBlur', // Validate on blur by default
    ...restOptions
  });
}
